// ServiceProcedureDrawer.jsx - Updated version

import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { FaIndianRupeeSign } from "react-icons/fa6";
import {
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  CheckIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { FaSearch } from "react-icons/fa";
import InfoTooltip from "../../components/common/InfoTooltip";
import ProductDrawer from "../../components/inventory/ProductDrawer";
import SearchableDropdown from "../../components/common/SearchableDropdown"; // ✅ Import this

// =========================================================
// TABS
// =========================================================

const TABS = [
  { id: "details", label: "Basic Details", hint: "Service information & pricing" },
  { id: "products", label: "Products", hint: "Products used by this service" },
  { id: "review", label: "Review", hint: "Confirm before saving" },
];

// =========================================================
// HELPERS
// =========================================================

const extractId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.$oid) return value.$oid;
  if (typeof value === "object" && value._id) return extractId(value._id);
  if (
    typeof value === "object" &&
    value.toString &&
    value.toString() !== "[object Object]"
  ) {
    return value.toString();
  }
  if (typeof value === "object" && value.id) return extractId(value.id);
  return null;
};

// =========================================================
// COMPONENT
// =========================================================

const ServiceProcedureDrawer = ({
  isOpen,
  procedureId,
  categories = [],
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  // =========================================================
  // CATEGORY - Searchable Dropdown State
  // =========================================================

  const [categorySearch, setCategorySearch] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const categoryDropdownRef = useRef(null);

  // =========================================================
  // FORM DATA
  // =========================================================

  const [formData, setFormData] = useState({
    name: "",
    client_friendly_name: "",
    category_id: "",
    description: "",

    fixed_price: 0,

    has_service_charge: false,
    tax_percentage: 0,

    status: "active",

    product_ids: [],
  });

  // =========================================================
  // PRODUCTS
  // =========================================================

  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const [selectedProducts, setSelectedProducts] = useState([]);

  // NEW: controls the "create a brand new product" drawer
  const [showAddProductDrawer, setShowAddProductDrawer] = useState(false);

  const productInputRef = useRef(null);
  const productDropdownRef = useRef(null);

  // =========================================================
  // LOAD DATA
  // =========================================================

  useEffect(() => {
    if (!isOpen) return;

    if (procedureId) {
      fetchProcedureDetails();
    } else {
      resetForm();
    }

    setActiveTab("details");
  }, [isOpen, procedureId]);

  // =========================================================
  // CLOSE PRODUCT DROPDOWN
  // =========================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // =========================================================
  // RESET FORM
  // =========================================================

  const resetForm = () => {
    setFormData({
      name: "",
      client_friendly_name: "",
      category_id: "",
      description: "",

      fixed_price: 0,

      has_service_charge: false,
      tax_percentage: 0,

      status: "active",

      product_ids: [],
    });

    setSelectedProducts([]);
    setProductSearchTerm("");
    setProductSearchResults([]);
    setShowProductDropdown(false);
    setShowAddProductDrawer(false);

    // Reset category state
    setCategorySearch("");
    setShowCategoryDropdown(false);

    setActiveTab("details");
  };

  // =========================================================
  // FETCH PROCEDURE DETAILS
  // =========================================================

  const fetchProcedureDetails = async () => {
    setLoading(true);

    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-service-procedure/${procedureId}`
      );

      if (response.code === 200) {
        const data = response.data;

        const categoryId = data.category_id ? extractId(data.category_id) : "";
        const productIds = (data.products || data.product_ids || [])
          .map((item) => extractId(item?.product_id ?? item))
          .filter(Boolean);

        setFormData({
          name: data.name || "",
          client_friendly_name: data.client_friendly_name || "",
          category_id: categoryId,
          description: data.description || "",

          fixed_price: data.fixed_price || 0,

          has_service_charge: data.has_service_charge || false,
          tax_percentage: data.tax_percentage || 0,

          status: data.status || "active",

          product_ids: productIds,
        });

        /* Load products */
        if (productIds.length > 0) {
          await fetchSelectedProducts(data.products || data.product_ids || []);
        } else {
          setSelectedProducts([]);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load procedure details");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FETCH SELECTED PRODUCTS
  // =========================================================

  const fetchSelectedProducts = async (productEntries) => {
    try {
      const products = await Promise.all(
        productEntries.map(async (entry) => {
          const id = extractId(entry?.product_id ?? entry);
          if (!id) return null;

          try {
            const response = await httpService.get(
              `/vendorproduct/getvendorproduct/${String(id)}`
            );

            if (response.code === 200) {
              const productData = response.data;

              return {
                ...productData,
                _id: productData._id || id,
                name: productData.variant_name || productData.name || "Unknown Product",
                sku_number: productData.sku_number || "",
                unit: productData.unit || "",
                price: productData.sale_price || productData.mrp || 0,
                quantity: Number(entry?.quantity) || 1,
                usage_qty: Number(entry?.usage_qty) || 1,
                usage_unit: entry?.usage_unit || "",
              };
            }

            return null;
          } catch (err) {
            console.error(`Failed to fetch product ${id}:`, err);
            return null;
          }
        })
      );

      setSelectedProducts(products.filter(Boolean));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load product details");
    }
  };

  // =========================================================
  // HANDLE FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // =========================================================
  // HANDLE PRICE INPUT - Replace on type, show 0 when empty
  // =========================================================

  const handlePriceChange = (e) => {
    const { name, value } = e.target;

    // If value is empty, set to 0
    if (value === "") {
      setFormData((prev) => ({ ...prev, [name]: 0 }));
      return;
    }

    // If value starts with 0 and has more digits, remove the leading zero
    if (value.startsWith('0') && value.length > 1) {
      const cleanedValue = value.replace(/^0+/, '');
      if (cleanedValue === '') {
        setFormData((prev) => ({ ...prev, [name]: 0 }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: parseFloat(cleanedValue) || 0 }));
      }
      return;
    }

    // Otherwise set the value
    setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  // =========================================================
  // CATEGORY HANDLERS - For SearchableDropdown
  // =========================================================

  const handleCreateCategory = async (name) => {
    if (!name?.trim()) {
      toast.error("Category name is required");
      return;
    }

    setCreatingCategory(true);
    try {
      const response = await httpService.post(
        `/vendorvetclinicservice/add-service-category`,
        { name: name.trim() }
      );

      if (response.status) {
        toast.success("Category created successfully!");
        
        // Refresh categories via onSuccess callback
        if (onSuccess) {
          await onSuccess();
        }
        
        // Get the new category ID from response
        const newCategoryId = response.data?.data?._id || response.data?._id;
        if (newCategoryId) {
          // Auto-select the newly created category
          setFormData((prev) => ({ ...prev, category_id: newCategoryId }));
        }
        
        // Clear search and close dropdown
        setCategorySearch("");
        setShowCategoryDropdown(false);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create category");
    } finally {
      setCreatingCategory(false);
    }
  };

  // =========================================================
  // PRODUCT SEARCH
  // =========================================================

  const searchProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      return;
    }

    setIsSearchingProducts(true);

    try {
      const response = await httpService.get(
        `/vendorproduct/searchvendorproducts?search=${encodeURIComponent(searchTerm)}`
      );

      if (response.code === 200) {
        const selectedIds = new Set(selectedProducts.map((p) => p._id));
        const filteredResults = response.data.filter((p) => !selectedIds.has(p._id));

        setProductSearchResults(filteredResults);
        setShowProductDropdown(true);
      }
    } catch (err) {
      console.error("Failed to search products:", err);
    } finally {
      setIsSearchingProducts(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchTerm) searchProducts(productSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearchTerm]);

  // =========================================================
  // ADD / UPDATE / REMOVE PRODUCT
  // =========================================================

  const handleAddProduct = (product) => {
    const price = product.sale_price || product.mrp || 0;

    setSelectedProducts((prev) => [
      ...prev,
      {
        ...product,
        name: product.variant_name || product.name,
        price,
        quantity: 1,
        usage_qty: 1,
        unit: product.unit || "",
        usage_unit: "",
      },
    ]);

    setFormData((prev) => ({
      ...prev,
      product_ids: [...prev.product_ids, product._id],
    }));

    setProductSearchTerm("");
    setProductSearchResults([]);
    setShowProductDropdown(false);

    if (productInputRef.current) productInputRef.current.focus();
  };

  // =========================================================
  // ADD A BRAND-NEW PRODUCT CREATED VIA ProductDrawer
  // =========================================================

  const handleNewProductCreated = (newProduct) => {
    setShowAddProductDrawer(false);

    if (!newProduct) return;

    // Case 1: simple product (no variants) — add it directly as one row
    if (!newProduct.has_variants) {
      handleAddProduct({
        _id: newProduct._id,
        name: newProduct.name,
        sku_number: newProduct.sku_number,
        unit: newProduct.unit,
        sale_price: newProduct.mrp,
        mrp: newProduct.mrp,
      });
      return;
    }

    // Case 2: product created with variants — add every variant as its own row
    const variants = newProduct.variants || [];
    if (variants.length === 0) {
      toast.info("Product created, but no variants were found to add.");
      return;
    }

    variants.forEach((variant) => {
      handleAddProduct({
        _id: variant._id || `${newProduct._id}-${variant.sku_number}`,
        name: `${newProduct.name}${variant.variant_name ? " - " + variant.variant_name : ""}`,
        sku_number: variant.sku_number,
        unit: variant.unit || newProduct.unit,
        sale_price: variant.mrp,
        mrp: variant.mrp,
      });
    });

    toast.success(`${variants.length} variant(s) added to this service`);
  };

  const handleUsageQtyInput = (productId, value) => {
    if (value === "") {
      setSelectedProducts((prev) =>
        prev.map((p) =>
          extractId(p._id) === extractId(productId) ? { ...p, usage_qty: "" } : p
        )
      );
      return;
    }

    const parsedValue = parseFloat(value);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      setSelectedProducts((prev) =>
        prev.map((p) =>
          extractId(p._id) === extractId(productId)
            ? { ...p, usage_qty: Math.max(0.1, parsedValue) }
            : p
        )
      );
    }
  };

  const handleProductQuantity = (productId, delta) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        extractId(p._id) === extractId(productId)
          ? {
              ...p,
              quantity: Math.max(1, Number(p.quantity || 1) + delta)
            }
          : p
      )
    );
  };

  const handleProductFieldChange = (productId, field, value) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        extractId(p._id) === extractId(productId) ? { ...p, [field]: value } : p
      )
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.filter((p) => extractId(p._id) !== extractId(productId))
    );

    setFormData((prev) => ({
      ...prev,
      product_ids: prev.product_ids.filter(
        (id) => extractId(id) !== extractId(productId)
      ),
    }));
  };

  // =========================================================
  // PRODUCT TOTAL
  // =========================================================

  const productsTotal = useMemo(() => {
    return selectedProducts.reduce(
      (sum, product) => sum + Number(product.price || 0) * Number(product.quantity || 1),
      0
    );
  }, [selectedProducts]);

  // =========================================================
  // VALIDATION
  // =========================================================

  const validateDetails = () => {
    if (!formData.name.trim()) {
      toast.error("Procedure name is required");
      return false;
    }

    if (!formData.fixed_price || Number(formData.fixed_price) <= 0) {
      toast.error("Price is required");
      return false;
    }

    return true;
  };

  // =========================================================
  // TAB NAVIGATION
  // =========================================================

  const goToTab = (tabId) => {
    if (tabId !== "details" && !validateDetails()) {
      setActiveTab("details");
      return;
    }

    setActiveTab(tabId);
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateDetails()) {
      setActiveTab("details");
      return;
    }

    const submitData = {
      ...formData,
      pricing_type: "fixed",
      products: selectedProducts.map((p) => ({
        product_id: p._id,
        quantity: Number(p.quantity) || 1,
        price: Number(p.price) || 0,
        usage_qty: Number(p.usage_qty) || 1,
        usage_unit: p.usage_unit || "",
      })),
      product_ids: selectedProducts.map((p) => p._id),
    };
    setLoading(true);

    try {
      const url = procedureId
        ? `/vendorvetclinicservice/update-service-procedure/${procedureId}`
        : "/vendorvetclinicservice/add-service-procedure";

      const method = procedureId ? "put" : "post";

      const response = await httpService[method](url, submitData);

      if (response.status) {
        toast.success(
          procedureId
            ? "Procedure updated successfully!"
            : "Procedure created successfully!"
        );

        onSuccess?.();
        onClose();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to save procedure");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  if (!isOpen) return null;

  const categoryName = categories.find((c) => c._id === formData.category_id)?.name || "—";
  const activeTabIndex = TABS.findIndex((t) => t.id === activeTab);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="relative w-[1100px] max-w-[98vw]">
          <div className="h-full flex flex-col bg-white shadow-xl">
            {/* HEADER - Shows Service Name and Price */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {procedureId ? "Edit Service" : "Add New Service"}
                    </h2>
                    {formData.name && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className="text-lg font-medium text-gray-800">
                          {formData.name}
                        </span>
                      </>
                    )}
                    {formData.name && formData.fixed_price > 0 && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className="text-lg font-medium text-blue-600 flex items-center gap-1">
                          ₹{formData.fixed_price}
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {activeTab === "details" && "Fill in the basic service information and pricing"}
                    {activeTab === "products" && "Add products used in this service"}
                    {activeTab === "review" && "Review all details before saving"}
                  </p>
                </div>

                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-4">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* BODY */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoaderSpiner text="Loading..." />
              </div>
            ) : (
              <form onSubmit={(e) => e.preventDefault()} className="flex-1 flex overflow-hidden">
                {/* SIDEBAR */}
                <div className="w-56 shrink-0 border-r border-gray-200 bg-gray-50 py-6 px-3">
                  <nav className="space-y-1">
                    {TABS.map((tab, idx) => {
                      const isActive = tab.id === activeTab;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => goToTab(tab.id)}
                          className={`w-full text-left px-3 py-3 rounded-lg flex items-start gap-3 ${
                            isActive
                              ? "bg-blue-50 border border-blue-200"
                              : "hover:bg-gray-100 border border-transparent"
                          }`}
                        >
                          <span
                            className={`mt-0.5 flex items-center justify-center h-6 w-6 rounded-full text-xs font-medium shrink-0 ${
                              isActive
                                ? "bg-blue-600 text-white"
                                : idx < activeTabIndex
                                ? "bg-green-500 text-white"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {idx < activeTabIndex ? (
                              <CheckIcon className="h-3.5 w-3.5" />
                            ) : (
                              idx + 1
                            )}
                          </span>

                          <span>
                            <div
                              className={`text-sm font-medium ${
                                isActive ? "text-blue-800" : "text-gray-800"
                              }`}
                            >
                              {tab.label}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">{tab.hint}</div>
                          </span>
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* CONTENT */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-1 overflow-y-auto px-4 py-2">
                    {/* ============ DETAILS ============ */}
                    {activeTab === "details" && (
                      <div className="max-w-3xl space-y-6">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            Service Information
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Define the service procedure and its pricing.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Procedure Name<span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="e.g. Deworming"
                              className="mt-1 block w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Client-Friendly Name
                            </label>
                            <input
                              type="text"
                              name="client_friendly_name"
                              value={formData.client_friendly_name}
                              onChange={handleChange}
                              placeholder="Display name for clients"
                              className="mt-1 block w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm"
                            />
                          </div>
                        </div>

                        {/* Pricing Section - Simplified */}
                        <div>
                          {/* Pricing Section - Price and Tax in same line */}
                          <div className="flex items-end gap-6">
                            {/* Price Input */}
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Price<span className="text-red-500">*</span>
                              </label>
                              <div className="relative mt-1">
                                <FaIndianRupeeSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                  type="text"
                                  name="fixed_price"
                                  value={formData.fixed_price}
                                  onChange={handlePriceChange}
                                  placeholder="Enter price"
                                  className="w-full pl-9 py-2.5 border border-gray-300 rounded-lg"
                                />
                              </div>
                            </div>

                            {/* Service Charges Section */}
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Do you want to add separate Service Charges?
                              </label>
                              <div className="flex items-center gap-3 mt-1">
                                <div className="flex items-center gap-3">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name="has_service_charge"
                                      checked={formData.has_service_charge === true}
                                      onChange={() => setFormData(prev => ({ ...prev, has_service_charge: true }))}
                                      className="h-4 w-4 text-blue-600"
                                    />
                                    <span className="text-sm text-gray-700">Yes</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name="has_service_charge"
                                      checked={formData.has_service_charge === false}
                                      onChange={() => setFormData(prev => ({ ...prev, has_service_charge: false, tax_percentage: 0 }))}
                                      className="h-4 w-4 text-blue-600"
                                    />
                                    <span className="text-sm text-gray-700">No</span>
                                  </label>
                                </div>

                                {formData.has_service_charge && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-700">%</span>
                                    <input
                                      type="number"
                                      name="tax_percentage"
                                      value={formData.tax_percentage}
                                      onChange={handlePriceChange}
                                      placeholder="0"
                                      className="w-20 border border-gray-300 rounded-lg py-2 px-3 text-sm"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* =========================================================
                            CATEGORY - Using SearchableDropdown
                        ========================================================= */}
                        <div>
                          <SearchableDropdown
                            ref={categoryDropdownRef}
                            label="Category"
                            value={formData.category_id}
                            onChange={(id) => {
                              setFormData((prev) => ({ ...prev, category_id: id || "" }));
                              if (id) {
                                setCategorySearch("");
                                setShowCategoryDropdown(false);
                              }
                            }}
                            options={categories}
                            searchValue={categorySearch}
                            setSearchValue={setCategorySearch}
                            showDropdown={showCategoryDropdown}
                            setShowDropdown={setShowCategoryDropdown}
                            loading={false}
                            onCreate={handleCreateCategory}
                            onDelete={null} // Optional: Add delete functionality if needed
                            isCreating={creatingCategory}
                            placeholder="Search or create category..."
                            createLabel="Create Category"
                            noResultsText="No category found"
                            inputClassName="mt-1"
                            required={false}
                            disabled={false}
                          />
                        </div>

                        {/* Description */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Describe what this procedure involves..."
                            className="mt-1 block w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm"
                          />
                        </div>

                        <div className="flex gap-1 bg-blue-50 border border-blue-100 rounded-lg p-1">
                          <InformationCircleIcon className="h-5 w-5 text-blue-600 shrink-0" />
                          <p className="text-sm text-blue-800">
                            Set the price to cover the complete service, including the cost of any products you add on the products tab.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* ============ PRODUCTS ============ */}
                    {activeTab === "products" && (
                      <div className="max-w-5xl space-y-6">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            Products Used
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Add products used while performing this service.
                          </p>
                        </div>

                        <div className="relative" ref={productDropdownRef}>
                          <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                              ref={productInputRef}
                              type="text"
                              value={productSearchTerm}
                              onChange={(e) => setProductSearchTerm(e.target.value)}
                              placeholder="Search product by name or SKU..."
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>

                          {showProductDropdown && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                              {isSearchingProducts ? (
                                <div className="p-4 text-sm text-gray-500">Searching products...</div>
                              ) : productSearchResults.length > 0 ? (
                                productSearchResults.map((product) => (
                                  <button
                                    key={product._id}
                                    type="button"
                                    onClick={() => handleAddProduct(product)}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between"
                                  >
                                    <div>
                                      <div className="font-medium text-sm text-gray-900">
                                        {product.variant_name || product.name}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        SKU: {product.sku_number || "N/A"} • Unit: {product.unit || "N/A"}
                                      </div>
                                    </div>
                                    <div className="font-medium text-sm text-gray-900">
                                      ₹{Number(product.sale_price || product.mrp || 0).toFixed(2)}
                                    </div>
                                  </button>
                                ))
                              ) : (
                                <div className="p-4 text-sm">
                                  <p className="text-gray-500">
                                    No products found{productSearchTerm ? ` for "${productSearchTerm}"` : ""}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowProductDropdown(false);
                                      setShowAddProductDrawer(true);
                                    }}
                                    className="mt-2 text-blue-600 font-medium hover:underline flex items-center gap-1"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Add {productSearchTerm ? `"${productSearchTerm}"` : ""} as a new product
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {selectedProducts.length > 0 ? (
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="min-w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Usage (Qty & Unit)</th>
                                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Quantity Used</th>
                                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Unit</th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Subtotal</th>
                                  <th className="px-3 py-2 w-12" />
                                </tr>
                              </thead>

                              <tbody className="divide-y divide-gray-100">
                                {selectedProducts.map((product) => {
                                  const usageQty = Number(product.usage_qty) || 1;
                                  const qtyUsed = Number(product.quantity) || 1;
                                  const subtotal = Number(product.price || 0) * qtyUsed;
                                  const productUnit = product.unit || product.product_unit || "";
                                  const usageUnit = product.usage_unit || "";

                                  return (
                                    <tr key={product._id}>
                                      <td className="px-3 py-2">
                                        <div className="font-medium text-sm text-gray-900">{product.name}</div>
                                        <div className="text-xs text-gray-500">
                                          SKU: {product.sku_number || "—"} | ₹{Number(product.price || 0).toFixed(2)}/{productUnit || "unit"}
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center justify-center gap-1">
                                          <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={usageQty}
                                            onChange={(e) => handleUsageQtyInput(product._id, e.target.value)}
                                            className="w-12 text-center border border-gray-300 rounded-md py-1 px-2 text-sm"
                                          />
                                          <span className="text-gray-400 text-sm">×</span>
                                          <select
                                            value={usageUnit}
                                            onChange={(e) => handleProductFieldChange(product._id, "usage_unit", e.target.value)}
                                            className="border border-gray-300 rounded-md py-1 px-2 text-sm bg-white w-16"
                                          >
                                            <option value="">Unit</option>
                                            <option value="ml">ml</option>
                                            <option value="l">L</option>
                                            <option value="gm">gm</option>
                                            <option value="kg">kg</option>
                                            <option value="mg">mg</option>
                                            <option value="vial">Vial</option>
                                            <option value="tablet">Tablet</option>
                                            <option value="capsule">Capsule</option>
                                            <option value="pcs">Pcs</option>
                                            <option value="bottle">Bottle</option>
                                            <option value="ampoule">Ampoule</option>
                                            <option value="syringe">Syringe</option>
                                            <option value="dose">Dose</option>
                                            <option value="drop">Drop</option>
                                            <option value="spray">Spray</option>
                                            <option value="tube">Tube</option>
                                            <option value="pack">Pack</option>
                                          </select>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center justify-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => handleProductQuantity(product._id, -1)}
                                            disabled={qtyUsed <= 1}
                                            className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                                          >
                                            <MinusIcon className="h-3.5 w-3.5" />
                                          </button>
                                          <span className="text-sm font-medium w-8 text-center">{qtyUsed}</span>
                                          <button
                                            type="button"
                                            onClick={() => handleProductQuantity(product._id, 1)}
                                            className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
                                          >
                                            <PlusIcon className="h-3.5 w-3.5" />
                                          </button>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 text-left">
                                        <span className="text-sm text-gray-700">{productUnit || "—"}</span>
                                      </td>
                                      <td className="px-3 py-2 text-right font-medium text-gray-900">
                                        ₹{subtotal.toFixed(2)}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveProduct(product._id)}
                                          className="text-gray-400 hover:text-red-600"
                                        >
                                          <TrashIcon className="h-5 w-5" />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>

                              <tfoot className="bg-gray-50">
                                <tr>
                                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-600">
                                    Estimated Product Cost
                                  </td>
                                  <td className="px-3 py-2 text-right font-semibold text-gray-900">
                                    ₹{productsTotal.toFixed(2)}
                                  </td>
                                  <td />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ) : (
                          <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center">
                            <p className="text-sm font-medium text-gray-700">No products added</p>
                            <p className="text-xs text-gray-500 mt-1">Search and add products used by this service.</p>
                          </div>
                        )}

                        <div className="flex gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4">
                          <InformationCircleIcon className="h-5 w-5 text-blue-600 shrink-0" />
                          <p className="text-sm text-blue-800">
                            Amount used here becomes the default quantity when this service is added to a Treatment Plan.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* ============ REVIEW ============ */}
                    {activeTab === "review" && (
                      <div className="max-w-4xl space-y-6">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            Review Service Procedure
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">Review everything before saving.</p>
                        </div>

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-5 py-3 font-semibold text-sm">Service Details</div>
                          <div className="divide-y divide-gray-100">
                            <div className="px-5 py-3 flex justify-between">
                              <span className="text-gray-500">Name</span>
                              <span className="font-medium">{formData.name || "—"}</span>
                            </div>
                            <div className="px-5 py-3 flex justify-between">
                              <span className="text-gray-500">Client Friendly Name</span>
                              <span>{formData.client_friendly_name || "—"}</span>
                            </div>
                            <div className="px-5 py-3 flex justify-between">
                              <span className="text-gray-500">Category</span>
                              <span>{categoryName}</span>
                            </div>
                            <div className="px-5 py-3 flex justify-between">
                              <span className="text-gray-500">Price</span>
                              <span className="text-right max-w-md">₹{formData.fixed_price || 0}
                                {formData.has_service_charge && formData.tax_percentage > 0 && (
                                  <span className="text-xs text-gray-500 ml-2">(+{formData.tax_percentage}% service charge)</span>
                                )}
                              </span>
                            </div>
                            <div className="px-5 py-3 flex justify-between">
                              <span className="text-gray-500">Description</span>
                              <span className="text-right max-w-md">{formData.description || "—"}</span>
                            </div>
                          </div>
                        </div>

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-5 py-3 font-semibold text-sm">
                            Products ({selectedProducts.length})
                          </div>
                          {selectedProducts.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                              {selectedProducts.map((product) => (
                                <div key={product._id} className="px-5 py-3 flex justify-between">
                                  <span>
                                    {product.name}
                                    <span className="text-gray-400 text-xs ml-2">
                                      ({product.quantity || 1} {product.unit || "unit"} × ₹
                                      {Number(product.price || 0).toFixed(2)})
                                    </span>
                                  </span>
                                  <span className="font-medium">
                                    ₹{(Number(product.price || 0) * Number(product.quantity || 1)).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                              <div className="px-5 py-3 flex justify-between bg-gray-50">
                                <span className="font-medium">Estimated Product Cost</span>
                                <span className="font-semibold">₹{productsTotal.toFixed(2)}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="px-5 py-4 text-sm text-gray-500">No products added</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* FOOTER */}
                  <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 bg-white">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      Cancel
                    </button>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        disabled={loading}
                        onClick={handleSubmit}
                        className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm disabled:opacity-50 hover:bg-green-700 transition-colors"
                      >
                        {loading ? "Saving..." : "Save"}
                      </button>

                      {activeTabIndex > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveTab(TABS[activeTabIndex - 1].id);
                          }}
                          className="px-5 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          Back
                        </button>
                      )}

                      {activeTabIndex < TABS.length - 1 ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            goToTab(TABS[activeTabIndex + 1].id);
                          }}
                          className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm"
                        >
                          Next
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={loading}
                          onClick={handleSubmit}
                          className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
                        >
                          {loading ? "Saving..." : procedureId ? "Update Service" : "Create Service"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Product Drawer for creating new products */}
      <ProductDrawer
        isOpen={showAddProductDrawer}
        onClose={() => setShowAddProductDrawer(false)}
        newProductName={productSearchTerm}
        onSave={handleNewProductCreated}
      />
    </div>
  );
};

export default ServiceProcedureDrawer;